/**
 * Template Quick Reply Area Component
 *
 * Handles interrupt UI for the template build chat feature.
 */

import React from 'react';
import type { InterruptState } from '@features/ai-chat-core';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Button, Chip, IconButton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAppDispatch } from '@store/hooks';

import {
  clearAllActivities,
  clearInterrupt,
  deselectAllOptions,
  selectAllOptions,
  setAssistantStatus,
  toggleOptionSelection,
} from '../store/chatSlice';

interface TemplateQuickReplyAreaProps {
  /**
   * Current interrupt state
   */
  interrupt: InterruptState | null;

  /**
   * Callback when user continues with selected options
   */
  onContinue: (selectedIds: string[]) => void;

  /**
   * Callback when user confirms/rejects
   */
  onConfirm?: (confirmed: boolean) => void;

  /**
   * Callback when interrupt is dismissed
   */
  onDismiss?: () => void;

  /**
   * Whether processing is in progress
   */
  isProcessing?: boolean;
}

export const TemplateQuickReplyArea: React.FC<TemplateQuickReplyAreaProps> = ({
  interrupt,
  onContinue,
  onConfirm,
  onDismiss,
  isProcessing = false,
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  if (!interrupt?.isActive) return null;

  const interruptType = interrupt.eventType;

  // Dismiss handler
  const handleDismiss = () => {
    if (interruptType === 'CONFIRMATION') {
      onConfirm?.(false);
    } else {
      dispatch(clearInterrupt());
      dispatch(clearAllActivities());
      dispatch(setAssistantStatus('idle'));
    }
    onDismiss?.();
  };

  // CONFIRMATION interrupt
  if (interruptType === 'CONFIRMATION') {
    const handleConfirm = () => onConfirm?.(true);
    const handleCancel = () => onConfirm?.(false);

    return (
      <Box
        sx={{
          position: 'relative',
          px: 2,
          pr: 5,
          py: 1.5,
          bgcolor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider}`,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <IconButton
          size="small"
          onClick={handleDismiss}
          disabled={isProcessing}
          aria-label="Dismiss"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'text.secondary',
            '&:hover': { color: 'text.primary' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {interrupt.message && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
            <InfoOutlinedIcon
              sx={{ fontSize: 20, color: theme.palette.primary.main, mt: 0.25 }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                {interrupt.message}
              </Typography>
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={isProcessing}
            data-testid="cancel-button"
          >
            {interrupt.cancelLabel || 'Cancel'}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={isProcessing}
            data-testid="confirm-button"
          >
            {isProcessing ? 'Processing...' : interrupt.confirmLabel || 'Confirm'}
          </Button>
        </Box>
      </Box>
    );
  }

  // MULTIPLE_CHOICE interrupt
  const options = interrupt.options || [];
  const selectedOptionIds = interrupt.selectedOptionIds || [];
  const allSelected = options.length > 0 && selectedOptionIds.length === options.length;

  const handleToggle = (optionId: string) => {
    dispatch(toggleOptionSelection(optionId));
  };

  const handleToggleSelectAll = () => {
    if (allSelected) {
      dispatch(deselectAllOptions());
    } else {
      dispatch(selectAllOptions());
    }
  };

  const handleContinue = () => {
    onContinue(selectedOptionIds);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        px: 2,
        pr: 5,
        py: 1.5,
        bgcolor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <IconButton
        size="small"
        onClick={handleDismiss}
        disabled={isProcessing}
        aria-label="Dismiss"
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          color: 'text.secondary',
          '&:hover': { color: 'text.primary' },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ display: 'block', mb: 1, fontWeight: 500 }}
      >
        {interrupt.question || 'Select options:'}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
        {options.map(option => {
          const isSelected = selectedOptionIds.includes(option.id);

          return (
            <Chip
              key={option.id}
              label={option.label}
              onClick={() => handleToggle(option.id)}
              color={isSelected ? 'primary' : 'default'}
              variant={isSelected ? 'filled' : 'outlined'}
              icon={isSelected ? <CheckIcon /> : undefined}
              sx={{
                fontSize: '0.875rem',
                fontWeight: isSelected ? 600 : 400,
                cursor: 'pointer',
                transition: theme.transitions.create(['all'], {
                  duration: theme.transitions.duration.short,
                }),
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: theme.shadows[2],
                },
              }}
              data-testid={`option-chip-${option.id}`}
            />
          );
        })}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography variant="caption" color="textSecondary">
            {selectedOptionIds.length > 0
              ? `${selectedOptionIds.length} selected`
              : `Select ${interrupt.minSelections ? `at least ${interrupt.minSelections}` : 'options'}`}
          </Typography>

          {(interrupt.allowSelectAll ?? options.length >= 2) && (
            <Button
              size="small"
              variant="text"
              onClick={handleToggleSelectAll}
              data-testid="select-all-button"
              sx={{ minWidth: 'auto', textTransform: 'none', fontSize: '0.75rem' }}
            >
              {allSelected ? 'Deselect All' : 'Select All'}
            </Button>
          )}
        </Box>

        <Button
          variant="contained"
          size="small"
          onClick={handleContinue}
          disabled={
            selectedOptionIds.length === 0 ||
            (interrupt.minSelections != null && selectedOptionIds.length < interrupt.minSelections) ||
            (interrupt.maxSelections != null && selectedOptionIds.length > interrupt.maxSelections)
          }
          data-testid="continue-button"
          sx={{ minWidth: 100 }}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );
};

